import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  invertedActionButtonStyles,
  semanticTone,
} from 'components/@home/@settings/Tabs/Agents/agenticThemeHelpers';
import CloseIcon from 'mdi-react/CloseIcon';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import { arrayOf, bool, func, object, string } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { I18n } from 'utils/i18n';

const getStyles = theme => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 700,
    padding: theme.spacing(2.5, 3, 1.5, 3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  content: {
    padding: theme.spacing(0, 3, 0, 3),
    '&.MuiDialogContent-root': { paddingTop: 0 },
  },
  toolbar: {
    display: 'flex',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  search: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: theme.palette.background.default,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
  },
  createBtn: {
    textTransform: 'none',
    borderRadius: '10px',
    borderColor: theme.palette.divider,
    color: theme.palette.text.primary,
    fontWeight: 600,
    paddingLeft: theme.spacing(1.75),
    paddingRight: theme.spacing(1.75),
    whiteSpace: 'nowrap',
  },
  list: {
    maxHeight: 420,
    overflowY: 'auto',
    marginLeft: theme.spacing(-0.5),
    marginRight: theme.spacing(-0.5),
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '12px',
    backgroundColor: theme.palette.background.paper,
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    '&:hover': {
      borderColor: theme.palette.text.disabled,
    },
  },
  rowSelected: {
    backgroundColor: theme.palette.background.default,
  },
  iconWrap: {
    width: 36,
    height: 36,
    borderRadius: '8px',
    backgroundColor: theme.palette.background.default,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  name: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
    color: theme.palette.text.primary,
    lineHeight: 1.3,
  },
  meta: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
    marginTop: '2px',
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  attachBtn: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: '8px',
    minWidth: 84,
    ...invertedActionButtonStyles(theme),
  },
  detachBtn: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: '8px',
    minWidth: 84,
    borderColor: theme.palette.divider,
    color: theme.palette.text.secondary,
    '&:hover': {
      borderColor: theme.palette.error.light,
      color: theme.palette.error.main,
      backgroundColor: semanticTone(theme, 'error').bg,
    },
  },
  emptyState: {
    padding: theme.spacing(5, 2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(13),
  },
  actions: {
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1),
  },
});

const AttachLibraryDialog = ({
  open,
  onClose,
  title,
  items,
  selectedIds = [],
  onSave,
  onCreate = null,
  createLabel = '',
  searchPlaceholder = '',
  emptyLabel = '',
  noMatchesLabel = '',
  renderIcon,
  renderMeta = null,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [search, setSearch] = useState('');
  const [localIds, setLocalIds] = useState([]);

  useEffect(() => {
    if (open) {
      setLocalIds((selectedIds || []).map(String));
      setSearch('');
    }
  }, [open, selectedIds]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      i =>
        (i.displayName || '').toLowerCase().includes(q) ||
        (i.name || '').toLowerCase().includes(q) ||
        (i.description || '').toLowerCase().includes(q),
    );
  }, [items, search]);

  const toggle = id => {
    const sid = String(id);
    setLocalIds(prev => (prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]));
  };

  const dirty = useMemo(() => {
    const base = (selectedIds || []).map(String).sort().join('|');
    const cur = [...localIds].sort().join('|');
    return base !== cur;
  }, [selectedIds, localIds]);

  const handleSave = () => {
    onSave(localIds);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: styles.paper } }}
    >
      <DialogTitle sx={styles.title} disableTypography>
        <Typography component="span" sx={{ fontSize: 18, fontWeight: 700 }}>
          {title}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label={I18n.t('Common.close')}>
          <CloseIcon size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.content}>
        <Box sx={styles.toolbar}>
          <TextField
            sx={styles.search}
            size="small"
            placeholder={searchPlaceholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <MagnifyIcon size={18} />
                  </InputAdornment>
                ),
              },
            }}
          />
          {onCreate && (
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<PlusIcon size={16} />}
              onClick={() => {
                onClose();
                onCreate();
              }}
            >
              {createLabel}
            </Button>
          )}
        </Box>
        <Box sx={styles.list}>
          {filtered.length === 0 && (
            <Box sx={styles.emptyState}>{items.length === 0 ? emptyLabel : noMatchesLabel}</Box>
          )}
          {filtered.map(item => {
            const attached = localIds.includes(String(item._id));
            return (
              <Box key={item._id} sx={[styles.row, attached && styles.rowSelected]}>
                <Box component="span" sx={styles.iconWrap}>
                  {renderIcon(item)}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={styles.name} noWrap>
                    {item.displayName || item.name}
                  </Typography>
                  {renderMeta && renderMeta(item) ? (
                    <Typography sx={styles.meta}>{renderMeta(item)}</Typography>
                  ) : null}
                </Box>
                {attached ? (
                  <Button
                    variant="outlined"
                    size="small"
                    color="secondary"
                    onClick={() => toggle(item._id)}
                  >
                    {I18n.t('AgenticChatFlow.Detach')}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    disableElevation
                    color="primary"
                    onClick={() => toggle(item._id)}
                  >
                    {I18n.t('AgenticChatFlow.Attach')}
                  </Button>
                )}
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Button variant="text" color="secondary" onClick={onClose}>
          {I18n.t('Common.Cancel')}
        </Button>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={!dirty}>
          {I18n.t('AgenticChatFlow.Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AttachLibraryDialog.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
  title: string.isRequired,
  items: arrayOf(object).isRequired,
  selectedIds: arrayOf(object),
  onSave: func.isRequired,
  onCreate: func,
  createLabel: string,
  searchPlaceholder: string,
  emptyLabel: string,
  noMatchesLabel: string,
  renderIcon: func.isRequired,
  renderMeta: func,
};

export default AttachLibraryDialog;
